import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, createBlock as _createBlock, isRef as _isRef, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue";
const _hoisted_1 = { class: "flex flex-col flex-1 min-h-0 p-5 gap-4" };
const _hoisted_2 = {
    key: 0,
    class: "flex flex-col items-center gap-3 rounded-xl border border-(--colors-border-error-subtle) bg-(--colors-background-error) p-8 text-center",
    "data-testid": "project-detail-error"
};
const _hoisted_3 = { class: "text-(--colors-text-error-strong)" };
const _hoisted_4 = { class: "flex flex-wrap gap-4 py-4" };
const _hoisted_5 = ["id", "aria-labelledby"];
const _hoisted_6 = { class: "grid grid-cols-1 lg:grid-cols-2 gap-5" };
const _hoisted_7 = { class: "flex flex-col gap-5" };
const _hoisted_8 = { class: "flex flex-col gap-4" };
const _hoisted_9 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-4" };
const _hoisted_10 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-4" };
const _hoisted_11 = { class: "flex flex-col gap-5" };
const _hoisted_12 = { class: "flex flex-col gap-4" };
const _hoisted_13 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-4" };
const _hoisted_14 = { class: "flex flex-col gap-1.5" };
const _hoisted_15 = { class: "text-sm font-medium text-(--colors-text-heading)" };
const _hoisted_16 = {
    class: "text-base font-semibold text-(--colors-text-heading)",
    "data-testid": "project-detail-collected-amount"
};
const _hoisted_17 = { class: "flex flex-col gap-1.5" };
const _hoisted_18 = { class: "text-sm font-medium text-(--colors-text-heading)" };
const _hoisted_19 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-4" };
const _hoisted_20 = {
    key: 0,
    class: "sticky bottom-5 mt-5 flex items-center justify-between gap-3 rounded-xl border border-(--colors-border-base-soft) bg-(--colors-background-white) dark:bg-(--colors-background-black-default) px-5 py-3 shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] dark:shadow-[0px_0px_4px_rgba(255,255,255,0.1)]",
    "data-testid": "project-detail-save-bar"
};
const _hoisted_21 = { class: "flex items-center gap-1.5 text-xs text-(--colors-text-body-subtle)" };
const _hoisted_22 = { class: "flex items-center gap-2" };
import { computed, nextTick, reactive, ref, toRef, useId, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Button, ButtonGroup, buttonGroupTabId, DatePickerInput, NavHeader, SelectInput, Textarea, TextInput, UnsavedChangesModal, WidgetShell, } from "@eudonet/opal";
import { logger } from "@/infrastructure/services/LoggerService";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useRefetchOnVisible } from "@/shared/composables/useRefetchOnVisible";
import { useProfileStore } from "@/modules/settings/public";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { useProjectAmounts } from "../composables/useProjectAmounts";
import { useSaveOrchestrator } from "@/shared/composables/forms/useSaveOrchestrator";
import { resolveApiErrorKey, getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { useDateIsoBinding } from "@/shared/composables/forms/useDateIsoBinding";
import { toDatePickerFormat } from "@/shared/utils/date-formatting";
import { getProject, getProjectCampaigns, getProjectDonations, updateProject, } from "../../infrastructure/api/projectsApi";
import { PROJECT_STATUSES, TERRITORIAL_SCOPES, } from "../../domain/entities/Project";
import { projectStatusTheme } from "../composables/projectStatusBadge";
import { toWireDate } from "../composables/wireDate";
import ProjectMilestonesWidget from "../components/detail/ProjectMilestonesWidget.vue";
import ProjectTabAllocations from "../components/detail/ProjectTabAllocations.vue";
import ProjectTabResume from "../components/detail/ProjectTabResume.vue";
/**
 * The active tab lives in the URL so the record is deep-linkable and a reload keeps the tab —
 * the creation flow already lands on `?tab=details`. Switching REPLACES the entry rather than
 * pushing one: back then leaves the record instead of walking the tabs, which is what the CRM
 * fiches do. Résumé is the default, so an unknown or absent value resolves to it rather than to
 * a blank surface.
 */
const RECENT_DONATIONS_SHOWN = 3;
// Page 1 holds the newest donations and is the only page the Résumé may show, so its rows are
// kept here rather than read back off `donations`, which follows the Affectations pager.
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProjectDetailView',
    setup(__props) {
        const PROJECT_TYPES = ["Donation", "Marketing", "Membership", "RecurringDonation"];
        const { t, locale } = useI18n();
        const route = useRoute();
        const router = useRouter();
        const { can } = usePermissions();
        const profileStore = useProfileStore();
        const { formatAmountForInput, parseAmount } = useFinanceFormatters();
        const { formatProjectAmount } = useProjectAmounts();
        const canEdit = computed(() => can("oproj.edit"));
        const project = ref(null);
        const headerBadges = computed(() => project.value
            ? [
                {
                    label: t(`projects.status.${project.value.status}`),
                    theme: projectStatusTheme[project.value.status] ?? "neutral",
                },
                { label: project.value.code, theme: "white" },
            ]
            : []);
        const loadError = ref(false);
        // ── Tabs ──────────────────────────────────────────────────────────────────────
        const RECORD_TABS = ["resume", "details", "affectations"];
        const activeTab = computed(() => {
            const requested = String(route.query.tab ?? "");
            return RECORD_TABS.includes(requested)
                ? requested
                : "resume";
        });
        const panelId = useId();
        function selectTab(tab) {
            if (tab === activeTab.value)
                return;
            void router.replace({ query: { ...route.query, tab } });
        }
        /** The Résumé's campaign widget is read-only, so "Voir tout" leaves for the campaigns list. */
        function goToCampaigns() {
            void router.push({ name: "CampaignsList" });
        }
        // ── Funding figures (OPDON, through the BFF) ──────────────────────────────────
        // The project row carries no live collected amount — only the donations allocated to it do, so
        // both the Résumé and the Détails budget block read this summary rather than the stored column.
        const donations = ref({
            items: [],
            totalDonations: 0,
            totalAllocated: 0,
            matchingDonations: 0,
            page: 1,
            pageSize: 0,
        });
        /** How many donations the Résumé's "Derniers dons" widget shows. */
        const recentDonationItems = ref([]);
        // Campaigns that claim this project (OPINK, through the BFF).
        const campaigns = ref({ items: [], totalCampaigns: 0 });
        // Each enrichment carries its OWN failure flag. Falling back to the zero-valued default would
        // otherwise present an unread funding figure as a project that collected nothing.
        const donationsError = ref(false);
        const campaignsError = ref(false);
        // True until the first attempt settles, so no widget renders its zero default in the meantime.
        const donationsLoading = ref(true);
        const campaignsLoading = ref(true);
        const milestonesWidget = ref(null);
        /** Résumé's "Ajouter un jalon": switch to Détails, then open that tab's creation modal. */
        async function openMilestoneCreationInDetails() {
            selectTab("details");
            await nextTick();
            milestonesWidget.value?.openCreate();
        }
        const recordActionItems = computed(() => [
            {
                label: t("projects.detail.actions.share"),
                icon: "ri-share-line",
                hasDividerAfter: true,
            },
            { label: t("projects.detail.actions.delete"), icon: "ri-delete-bin-line", danger: true },
        ]);
        // ── Edit form (single shared state for the three form sections) ───────────────
        const form = reactive({
            name: "",
            code: "",
            projectType: "",
            status: "Idea",
            startDate: "",
            endDate: "",
            description: "",
            actionDomain: "",
            geographicArea: "",
            territorialScope: "",
            targetAudience: "",
            fundingSource: "",
            chargeAccount: "",
            analyticalCode: "",
            financialComments: "",
        });
        const objectiveAmountInput = ref("");
        const estimatedBeneficiariesInput = ref("");
        const errors = reactive({
            name: "",
            code: "",
            actionDomain: "",
            objectiveAmount: "",
            estimatedBeneficiaries: "",
        });
        const dateFormat = computed(() => toDatePickerFormat(profileStore.profile?.dateFormat, locale.value));
        const startDateDisplay = useDateIsoBinding(toRef(form, "startDate"), computed(() => profileStore.profile?.dateFormat), locale);
        const endDateDisplay = useDateIsoBinding(toRef(form, "endDate"), computed(() => profileStore.profile?.dateFormat), locale);
        const statusOptions = computed(() => PROJECT_STATUSES.map((status) => ({
            value: status,
            label: t(`projects.status.${status}`),
            badgeTheme: statusTheme(status),
        })));
        function statusTheme(value) {
            return projectStatusTheme[value] ?? "neutral";
        }
        const projectTypeOptions = computed(() => [
            { value: "", label: "–" },
            ...PROJECT_TYPES.map((type) => ({ value: type, label: t(`projects.type.${type}`) })),
        ]);
        const territorialScopeOptions = computed(() => [
            { value: "", label: "–" },
            ...TERRITORIAL_SCOPES.map((scope) => ({
                value: scope,
                label: t(`projects.territorialScope.${scope}`),
            })),
        ]);
        function seedForm(detail) {
            form.name = detail.name;
            form.code = detail.code;
            form.projectType = detail.projectType ?? "";
            form.status = detail.status;
            form.startDate = detail.startDate?.slice(0, 10) ?? "";
            form.endDate = detail.endDate?.slice(0, 10) ?? "";
            form.description = detail.description ?? "";
            form.actionDomain = detail.actionDomain ?? "";
            form.geographicArea = detail.geographicArea ?? "";
            form.territorialScope = detail.territorialScope ?? "";
            form.targetAudience = detail.targetAudience ?? "";
            form.fundingSource = detail.fundingSource ?? "";
            form.chargeAccount = detail.chargeAccount ?? "";
            form.analyticalCode = detail.analyticalCode ?? "";
            form.financialComments = detail.financialComments ?? "";
            objectiveAmountInput.value = formatAmountForInput(detail.objectiveAmount);
            estimatedBeneficiariesInput.value =
                detail.estimatedBeneficiaries != null ? String(detail.estimatedBeneficiaries) : "";
            errors.name = "";
            errors.code = "";
            // The action domain became mandatory after the tenant's default fund was seeded without one,
            // so it can arrive empty: surface the error on load, or the save bar shows a permanently
            // disabled Enregistrer with nothing pointing at the field that blocks it.
            errors.actionDomain = form.actionDomain.trim()
                ? ""
                : t("projects.create.errors.actionDomainRequired");
            errors.objectiveAmount = "";
            errors.estimatedBeneficiaries = "";
            snapshot.value = serializeForm();
        }
        function serializeForm() {
            return JSON.stringify({
                ...form,
                objectiveAmount: objectiveAmountInput.value,
                estimatedBeneficiaries: estimatedBeneficiariesInput.value,
            });
        }
        const snapshot = ref("");
        const formDirty = computed(() => snapshot.value !== "" && serializeForm() !== snapshot.value);
        // "Annuler" on the save bar hides it WITHOUT restoring the fields; any further edit re-arms it.
        const saveBarDismissed = ref(false);
        watch(formDirty, (dirty) => {
            if (!dirty)
                saveBarDismissed.value = false;
        });
        watch(serializeForm, () => {
            saveBarDismissed.value = false;
        });
        const showSaveBar = computed(() => canEdit.value && formDirty.value && !saveBarDismissed.value);
        // ── Validation ────────────────────────────────────────────────────────────────
        const endDateError = computed(() => form.startDate && form.endDate && form.endDate < form.startDate
            ? t("projects.create.errors.endDateBeforeStart")
            : "");
        const parsedObjectiveAmount = computed(() => {
            const raw = objectiveAmountInput.value.trim();
            if (!raw)
                return null;
            const parsed = parseAmount(raw);
            return Number.isFinite(parsed) ? parsed : Number.NaN;
        });
        const parsedEstimatedBeneficiaries = computed(() => {
            const raw = estimatedBeneficiariesInput.value.trim();
            if (!raw)
                return null;
            const parsed = Number(raw);
            return Number.isInteger(parsed) ? parsed : Number.NaN;
        });
        function validateName() {
            errors.name = form.name.trim() ? "" : t("projects.create.errors.nameRequired");
        }
        function validateCode() {
            errors.code = form.code.trim() ? "" : t("projects.detail.errors.codeRequired");
        }
        function validateActionDomain() {
            errors.actionDomain = form.actionDomain.trim()
                ? ""
                : t("projects.create.errors.actionDomainRequired");
        }
        function validateObjectiveAmount() {
            // Same rule as the create modal and the backend validator (>= 0): rejecting 0 here would
            // make a project stored with a 0 objective unable to save ANY edit.
            const value = parsedObjectiveAmount.value;
            errors.objectiveAmount =
                value !== null && (Number.isNaN(value) || value < 0)
                    ? t("projects.create.errors.objectiveAmountInvalid")
                    : "";
        }
        function validateEstimatedBeneficiaries() {
            const value = parsedEstimatedBeneficiaries.value;
            errors.estimatedBeneficiaries =
                value !== null && (Number.isNaN(value) || value < 0)
                    ? t("projects.detail.errors.estimatedBeneficiariesInvalid")
                    : "";
        }
        // The code-conflict error comes from the server, so no blur validator ever recomputes it.
        watch(() => form.code, () => {
            errors.code = "";
        });
        // A load-time action-domain error (see seedForm) is on screen before the field is ever focused,
        // and the only control that could blur it is the save button the error itself keeps disabled —
        // so clear it on input instead of waiting for the blur validator.
        watch(() => form.actionDomain, (value) => {
            if (value.trim())
                errors.actionDomain = "";
        });
        const isFormValid = computed(() => !!form.name.trim() &&
            !!form.code.trim() &&
            !!form.actionDomain.trim() &&
            !endDateError.value &&
            !errors.name &&
            !errors.code &&
            !errors.actionDomain &&
            !errors.objectiveAmount &&
            !errors.estimatedBeneficiaries);
        // ── Budget derived values ─────────────────────────────────────────────────────
        // Same rule as the Résumé: until the funding read settles — and for good if it fails — the amount
        // is UNKNOWN, so the Détails budget block shows a dash rather than a 0 € that reads as a project
        // nobody has given to.
        const collectedAmountUnknown = computed(() => donationsError.value || donationsLoading.value);
        const collectedAmountDisplay = computed(() => collectedAmountUnknown.value ? "—" : formatProjectAmount(donations.value.totalAllocated));
        // Écart vs objectif = collecté - objectif; "—" without an objective, or without a collected amount.
        const variance = computed(() => {
            const objective = parsedObjectiveAmount.value;
            if (collectedAmountUnknown.value || objective === null || Number.isNaN(objective)) {
                return null;
            }
            return donations.value.totalAllocated - objective;
        });
        const varianceDisplay = computed(() => variance.value === null ? "—" : formatProjectAmount(variance.value));
        // ── Save orchestration (single bloc: the three form sections share one PUT) ───
        const showUnsavedChangesModal = ref(false);
        // The orchestrator toasts blocErrorToastKey(id) on a rejected save; routing it through this ref
        // lets the toast carry the errorCode-resolved message instead of a generic one.
        const lastSaveErrorKey = ref(null);
        const UPDATE_ERROR_KEYS = {
            "Project.Name.Required": "projects.create.errors.nameRequired",
            "Project.Name.TooLong": "projects.create.errors.nameTooLong",
            "Project.Code.Required": "projects.detail.errors.codeRequired",
            "Project.Code.TooLong": "projects.create.errors.codeTooLong",
            "Project.Code.AlreadyExists": "projects.create.errors.codeTaken",
            "Project.ActionDomain.Required": "projects.create.errors.actionDomainRequired",
            "Project.ActionDomain.TooLong": "projects.create.errors.actionDomainTooLong",
            "Project.Type.Invalid": "projects.detail.errors.typeInvalid",
            "Project.Status.Required": "projects.create.errors.statusInvalid",
            "Project.Status.Invalid": "projects.create.errors.statusInvalid",
            "Project.Description.TooLong": "projects.create.errors.descriptionTooLong",
            "Project.ObjectiveAmount.Invalid": "projects.create.errors.objectiveAmountInvalid",
            "Project.EndDate.BeforeStartDate": "projects.create.errors.endDateBeforeStart",
            "Project.FundingSource.TooLong": "projects.create.errors.fundingSourceTooLong",
            "Project.ChargeAccount.TooLong": "projects.create.errors.chargeAccountTooLong",
            "Project.AnalyticalCode.TooLong": "projects.create.errors.analyticalCodeTooLong",
            "Project.GeographicArea.TooLong": "projects.detail.errors.geographicAreaTooLong",
            "Project.TargetAudience.TooLong": "projects.detail.errors.targetAudienceTooLong",
            "Project.FinancialComments.TooLong": "projects.detail.errors.financialCommentsTooLong",
            "Project.TerritorialScope.Invalid": "projects.detail.errors.territorialScopeInvalid",
            "Project.EstimatedBeneficiaries.Invalid": "projects.detail.errors.estimatedBeneficiariesInvalid",
        };
        const { isDirty, isSaving, register, saveAll, proceedNavigation, cancelNavigation } = useSaveOrchestrator({
            successToastKey: "projects.detail.toastUpdated",
            blocErrorToastKey: () => lastSaveErrorKey.value ?? "projects.detail.saveError",
            onNavigationBlocked: () => {
                showUnsavedChangesModal.value = true;
            },
        });
        async function saveProject() {
            if (!project.value)
                return;
            validateName();
            validateCode();
            validateActionDomain();
            validateObjectiveAmount();
            validateEstimatedBeneficiaries();
            if (!isFormValid.value) {
                lastSaveErrorKey.value = "projects.detail.saveError";
                throw new Error("Project details form is invalid");
            }
            try {
                const updated = await updateProject(project.value.projectId, {
                    name: form.name.trim(),
                    code: form.code.trim(),
                    projectType: form.projectType || null,
                    status: form.status,
                    objectiveAmount: parsedObjectiveAmount.value,
                    startDate: toWireDate(form.startDate),
                    endDate: toWireDate(form.endDate),
                    description: form.description.trim() || null,
                    actionDomain: form.actionDomain.trim(),
                    fundingSource: form.fundingSource.trim() || null,
                    chargeAccount: form.chargeAccount.trim() || null,
                    analyticalCode: form.analyticalCode.trim() || null,
                    geographicArea: form.geographicArea.trim() || null,
                    territorialScope: (form.territorialScope || null),
                    estimatedBeneficiaries: parsedEstimatedBeneficiaries.value,
                    targetAudience: form.targetAudience.trim() || null,
                    financialComments: form.financialComments.trim() || null,
                });
                project.value = updated;
                seedForm(project.value);
            }
            catch (err) {
                lastSaveErrorKey.value = resolveApiErrorKey(err, UPDATE_ERROR_KEYS, "projects.detail.saveError");
                if (getApiErrorCode(err) === "Project.Code.AlreadyExists") {
                    errors.code = t("projects.create.errors.codeTaken");
                }
                throw err;
            }
        }
        register({ id: "details", isDirty: formDirty, save: saveProject });
        function goBackToList() {
            // A plain push: the orchestrator's route-leave guard owns the dirty check.
            void router.push({ name: "ProjectList" });
        }
        function handleLeave() {
            showUnsavedChangesModal.value = false;
            proceedNavigation();
        }
        function handleStay() {
            showUnsavedChangesModal.value = false;
            cancelNavigation();
        }
        // Failing blocs stay dirty (saveAll toasts), so the modal stays open for a retry.
        async function handleSaveAndContinue() {
            await saveAll();
            if (isDirty.value)
                return;
            showUnsavedChangesModal.value = false;
            proceedNavigation();
        }
        // ── Load ──────────────────────────────────────────────────────────────────────
        async function load() {
            loadError.value = false;
            const projectId = String(route.params.projectId);
            try {
                const detail = await getProject(projectId);
                project.value = detail;
                seedForm(detail);
            }
            catch (e) {
                loadError.value = true;
                logger.error("Project detail load failed", e);
                return;
            }
            // Separate tries: the funding figures and the campaigns are enrichments, so losing either degrades
            // its widget to an empty state rather than replacing the whole record with the load-error
            // screen (R115). They run concurrently — neither depends on the other.
            await Promise.allSettled([loadDonations(projectId), loadCampaigns(projectId)]);
        }
        // One read serves both surfaces: the Résumé shows the three most recent of the default read, the
        // Affectations tab whatever page/filter/sort it asked for last. The default read is the tab's own
        // first page, so opening the tab costs no second round-trip.
        const DEFAULT_ALLOCATIONS_QUERY = { page: 1, pageSize: 10 };
        // The query the Affectations tab last read with, kept across a FAILED read so Réessayer re-runs
        // the read that failed. Derived from `donations` it would fall back to the last read that
        // succeeded, and the user would silently land on page 1 after retrying page 4.
        const allocationsQuery = ref({ ...DEFAULT_ALLOCATIONS_QUERY });
        /** Newest first, nothing filtered, first page — the only read whose rows are the "Derniers dons". */
        function isNewestFirstPage(query) {
            return (query.page === 1 &&
                !query.donor &&
                (!query.sortBy || (query.sortBy === "Date" && query.sortOrder !== "asc")));
        }
        // Only the latest read may write: paging or searching fast fires overlapping reads, and a slow
        // earlier response would otherwise overwrite the newer rows, pill and pager (R97).
        let donationsRun = 0;
        /**
         * `silent`: a background refresh of figures already on screen. It neither raises the loading
         * state (the Résumé would swap its widgets for skeletons and blank the KPI tiles on every return
         * to the tab) nor the error state on failure (the figures shown are the last read ones, which
         * beats an error screen for a refresh the user did not ask for); the failure is still logged.
         */
        async function loadDonations(projectId = String(route.params.projectId), query = allocationsQuery.value, { silent = false } = {}) {
            const run = ++donationsRun;
            allocationsQuery.value = query;
            if (!silent)
                donationsLoading.value = true;
            try {
                const summary = await getProjectDonations(projectId, query);
                if (run !== donationsRun)
                    return;
                donations.value = summary;
                if (isNewestFirstPage(query)) {
                    recentDonationItems.value = summary.items.slice(0, RECENT_DONATIONS_SHOWN);
                }
                donationsError.value = false;
            }
            catch (e) {
                if (run !== donationsRun)
                    return;
                if (!silent)
                    donationsError.value = true;
                logger.error("Project donations load failed", e);
            }
            finally {
                // Cleared whenever this run is the latest, silent or not: a silent refresh that overtakes a
                // visible read would otherwise leave the loading state raised for good.
                if (run === donationsRun)
                    donationsLoading.value = false;
            }
        }
        async function loadCampaigns(projectId = String(route.params.projectId)) {
            campaignsLoading.value = true;
            try {
                campaigns.value = await getProjectCampaigns(projectId);
                campaignsError.value = false;
            }
            catch (e) {
                campaignsError.value = true;
                logger.error("Project campaigns load failed", e);
            }
            finally {
                campaignsLoading.value = false;
            }
        }
        /**
         * CONSTRAINT: the Résumé's three rows are captured when the newest-first first page is read and
         * are NOT re-derived from `donations`, which follows the Affectations tab's pager, filters and
         * sort. Deriving them again empties or reorders the widget on a funded project the moment the
         * user pages or filters the tab, and no existing test fails to say so.
         */
        const recentDonations = computed(() => ({
            ...donations.value,
            items: recentDonationItems.value,
        }));
        /** Paging, filtering or sorting the Affectations tab re-reads; the totals come back unchanged. */
        function changeAllocationsQuery(query) {
            void loadDonations(String(route.params.projectId), query);
        }
        /** The Résumé only ever shows the newest first page, so its own retry asks for that, not the tab's. */
        function retryRecentDonations() {
            void loadDonations(String(route.params.projectId), { ...DEFAULT_ALLOCATIONS_QUERY });
        }
        // Latest-wins guard of the Résumé-only read below, separate from `donationsRun`: the two reads
        // write different state and must not cancel each other.
        let recentDonationsRun = 0;
        /**
         * Re-reads the newest-first first page for the Résumé's "Derniers dons" ONLY, when the tab's
         * own query is not that page (paged, filtered or sorted away): `loadDonations` then refreshes the
         * totals but leaves the three rows untouched. Writes nothing else — not `donations`, not the
         * tab's query — and keeps the current rows on failure, which is logged.
         */
        async function refreshRecentDonations(projectId) {
            const run = ++recentDonationsRun;
            try {
                const summary = await getProjectDonations(projectId, { ...DEFAULT_ALLOCATIONS_QUERY });
                if (run !== recentDonationsRun)
                    return;
                recentDonationItems.value = summary.items.slice(0, RECENT_DONATIONS_SHOWN);
            }
            catch (e) {
                if (run !== recentDonationsRun)
                    return;
                logger.error("Project recent donations refresh failed", e);
            }
        }
        // A donation is allocated from ITS record, so a project record left open in another browser tab
        // keeps stale figures until the user comes back to it: re-read them then, with the tab's own
        // query so the Affectations page, filter and sort survive the refresh (the run id drops a late
        // earlier response). Only once the record itself has loaded — before that, `load` owns the read.
        // Silent only while no read the user asked for is in flight: the refresh takes over its run id
        // and drops its response, so on failure it must also take over its error state — the user paged
        // to page 3 and would otherwise keep page 2's rows under page 3's pager with nothing shown.
        useRefetchOnVisible(() => {
            if (!project.value)
                return;
            const projectId = String(route.params.projectId);
            void loadDonations(projectId, allocationsQuery.value, { silent: !donationsLoading.value });
            if (!isNewestFirstPage(allocationsQuery.value))
                void refreshRecentDonations(projectId);
        });
        // Milestone writes update the list from the write's OWN response first — the row must appear
        // even if the follow-up refetch fails — then a background refetch restores the server's
        // target-date ordering. Only the list is touched, never the form (the user may be mid-edit;
        // overwriting the form from the refetch would silently discard their changes).
        function onMilestoneSaved(milestone) {
            if (!project.value)
                return;
            const index = project.value.milestones.findIndex((m) => m.milestoneId === milestone.milestoneId);
            if (index === -1) {
                project.value.milestones = [...project.value.milestones, milestone];
            }
            else {
                project.value.milestones = project.value.milestones.map((m) => m.milestoneId === milestone.milestoneId ? milestone : m);
            }
            void reloadMilestones();
        }
        function onMilestoneDeleted(milestoneId) {
            if (!project.value)
                return;
            project.value.milestones = project.value.milestones.filter((m) => m.milestoneId !== milestoneId);
            // Invalidate any in-flight refresh: its response predates the delete and would
            // resurrect the removed row.
            milestonesRefreshSeq++;
        }
        // Only the latest refresh may write the list — a slower earlier response would
        // overwrite it with a snapshot missing the just-saved milestone.
        let milestonesRefreshSeq = 0;
        async function reloadMilestones() {
            if (!project.value)
                return;
            const seq = ++milestonesRefreshSeq;
            try {
                const fresh = await getProject(project.value.projectId);
                if (seq !== milestonesRefreshSeq || !project.value)
                    return;
                project.value.milestones = fresh.milestones;
            }
            catch (e) {
                logger.error("Milestones refresh failed", e);
            }
        }
        watch(() => route.params.projectId, load, { immediate: true });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (loadError.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)("projects.detail.loadError")), 1 /* TEXT */),
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _unref(t)('projects.detail.retry'),
                            onClick: load
                        }, null, 8 /* PROPS */, ["label"])
                    ]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createVNode(_unref(NavHeader), {
                            type: "record",
                            title: project.value?.name ?? '…',
                            back: { label: _unref(t)('projects.detail.backToList'), href: _unref(router).resolve({ name: 'ProjectList' }).href },
                            badges: headerBadges.value,
                            "primary-action": {
                                label: _unref(t)('projects.detail.actions.allocateDonation'),
                                icon: 'ri-hand-coin-line',
                            },
                            "secondary-action": { label: _unref(t)('projects.detail.actions.edit'), icon: 'ri-edit-line' },
                            "icon-actions": [
                                { icon: 'ri-more-2-fill', label: _unref(t)('projects.detail.actions.menu'), menu: recordActionItems.value },
                            ],
                            onBack: goBackToList,
                            onSecondaryAction: _cache[0] || (_cache[0] = ($event) => (selectTab('details')))
                        }, null, 8 /* PROPS */, ["title", "back", "badges", "primary-action", "secondary-action", "icon-actions"]),
                        _createElementVNode("div", _hoisted_4, [
                            _createVNode(_unref(ButtonGroup), {
                                "aria-label": _unref(t)('projects.detail.tabs.ariaLabel'),
                                "model-value": activeTab.value,
                                "panel-id": _unref(panelId),
                                "overflow-label": _unref(t)('common.tabs.next'),
                                "overflow-previous-label": _unref(t)('common.tabs.previous'),
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (selectTab($event)))
                            }, {
                                default: _withCtx(() => [
                                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(RECORD_TABS, (tab) => {
                                        return _createVNode(_unref(Button), {
                                            key: tab,
                                            variant: "tertiary",
                                            size: "sm",
                                            label: _unref(t)(`projects.detail.tabs.${tab}`),
                                            value: tab,
                                            "data-testid": `project-detail-tab-${tab}`
                                        }, null, 8 /* PROPS */, ["label", "value", "data-testid"]);
                                    }), 64 /* STABLE_FRAGMENT */))
                                ]),
                                _: 1 /* STABLE */
                            }, 8 /* PROPS */, ["aria-label", "model-value", "panel-id", "overflow-label", "overflow-previous-label"])
                        ]),
                        (activeTab.value === 'resume' && project.value)
                            ? (_openBlock(), _createBlock(ProjectTabResume, {
                                key: 0,
                                id: _unref(panelId),
                                role: "tabpanel",
                                "aria-labelledby": _unref(buttonGroupTabId)(_unref(panelId), activeTab.value),
                                project: project.value,
                                donations: recentDonations.value,
                                campaigns: campaigns.value,
                                "donations-error": donationsError.value,
                                "campaigns-error": campaignsError.value,
                                "donations-loading": donationsLoading.value,
                                "campaigns-loading": campaignsLoading.value,
                                "can-edit": canEdit.value,
                                onAddMilestone: openMilestoneCreationInDetails,
                                onGoToTab: selectTab,
                                onRetryDonations: retryRecentDonations,
                                onRetryCampaigns: _cache[2] || (_cache[2] = ($event) => (loadCampaigns())),
                                onGoToCampaigns: goToCampaigns
                            }, null, 8 /* PROPS */, ["id", "aria-labelledby", "project", "donations", "campaigns", "donations-error", "campaigns-error", "donations-loading", "campaigns-loading", "can-edit"]))
                            : _createCommentVNode("v-if", true),
                        (activeTab.value === 'affectations' && project.value)
                            ? (_openBlock(), _createBlock(ProjectTabAllocations, {
                                key: 1,
                                id: _unref(panelId),
                                role: "tabpanel",
                                "aria-labelledby": _unref(buttonGroupTabId)(_unref(panelId), activeTab.value),
                                "project-id": project.value.projectId,
                                "project-name": project.value.name,
                                donations: donations.value,
                                query: allocationsQuery.value,
                                loading: donationsLoading.value,
                                error: donationsError.value,
                                onRetry: _cache[3] || (_cache[3] = ($event) => (loadDonations())),
                                onQueryChange: changeAllocationsQuery
                            }, null, 8 /* PROPS */, ["id", "aria-labelledby", "project-id", "project-name", "donations", "query", "loading", "error"]))
                            : _createCommentVNode("v-if", true),
                        _withDirectives(_createElementVNode("div", {
                            id: activeTab.value === 'details' ? _unref(panelId) : undefined,
                            role: "tabpanel",
                            "aria-labelledby": _unref(buttonGroupTabId)(_unref(panelId), 'details'),
                            class: "mb-5"
                        }, [
                            _createElementVNode("div", _hoisted_6, [
                                _createElementVNode("div", _hoisted_7, [
                                    _createVNode(_unref(WidgetShell), {
                                        header: _unref(t)('projects.detail.sections.qualification')
                                    }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_8, [
                                                _createVNode(_unref(TextInput), {
                                                    modelValue: form.name,
                                                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((form.name) = $event)),
                                                    label: _unref(t)('projects.create.nameLabel'),
                                                    required: "",
                                                    maxlength: 200,
                                                    error: errors.name,
                                                    disabled: !canEdit.value,
                                                    "full-width": "",
                                                    "data-testid": "project-detail-name",
                                                    onBlur: validateName
                                                }, null, 8 /* PROPS */, ["modelValue", "label", "error", "disabled"]),
                                                _createElementVNode("div", _hoisted_9, [
                                                    _createVNode(_unref(TextInput), {
                                                        modelValue: form.code,
                                                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((form.code) = $event)),
                                                        label: _unref(t)('projects.create.codeLabel'),
                                                        required: "",
                                                        maxlength: 50,
                                                        error: errors.code,
                                                        disabled: !canEdit.value,
                                                        "full-width": "",
                                                        "data-testid": "project-detail-code-input",
                                                        onBlur: validateCode
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "error", "disabled"]),
                                                    _createVNode(_unref(SelectInput), {
                                                        modelValue: form.projectType,
                                                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((form.projectType) = $event)),
                                                        label: _unref(t)('projects.detail.typeLabel'),
                                                        options: projectTypeOptions.value,
                                                        disabled: !canEdit.value,
                                                        "full-width": "",
                                                        "data-testid": "project-detail-type"
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "options", "disabled"]),
                                                    _createVNode(_unref(TextInput), {
                                                        modelValue: form.actionDomain,
                                                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((form.actionDomain) = $event)),
                                                        label: _unref(t)('projects.create.actionDomainLabel'),
                                                        required: "",
                                                        maxlength: 100,
                                                        error: errors.actionDomain,
                                                        disabled: !canEdit.value,
                                                        "full-width": "",
                                                        "data-testid": "project-detail-action-domain",
                                                        onBlur: validateActionDomain
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "error", "disabled"]),
                                                    _createVNode(_unref(SelectInput), {
                                                        modelValue: form.status,
                                                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((form.status) = $event)),
                                                        label: _unref(t)('projects.create.statusLabel'),
                                                        required: "",
                                                        options: statusOptions.value,
                                                        disabled: !canEdit.value,
                                                        "full-width": "",
                                                        "data-testid": "project-detail-status-select"
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "options", "disabled"]),
                                                    _createVNode(_unref(DatePickerInput), {
                                                        modelValue: _unref(startDateDisplay),
                                                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => (_isRef(startDateDisplay) ? (startDateDisplay).value = $event : null)),
                                                        label: _unref(t)('projects.create.startDateLabel'),
                                                        format: dateFormat.value,
                                                        language: _unref(locale),
                                                        "week-start": 1,
                                                        disabled: !canEdit.value,
                                                        "full-width": "",
                                                        "data-testid": "project-detail-start-date"
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language", "disabled"]),
                                                    _createVNode(_unref(DatePickerInput), {
                                                        modelValue: _unref(endDateDisplay),
                                                        "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event) => (_isRef(endDateDisplay) ? (endDateDisplay).value = $event : null)),
                                                        label: _unref(t)('projects.create.endDateLabel'),
                                                        format: dateFormat.value,
                                                        language: _unref(locale),
                                                        "week-start": 1,
                                                        error: endDateError.value,
                                                        disabled: !canEdit.value,
                                                        "full-width": "",
                                                        "data-testid": "project-detail-end-date"
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language", "error", "disabled"])
                                                ]),
                                                _createVNode(_unref(Textarea), {
                                                    modelValue: form.description,
                                                    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event) => ((form.description) = $event)),
                                                    label: _unref(t)('projects.detail.descriptionLabel'),
                                                    rows: 3,
                                                    height: "auto",
                                                    disabled: !canEdit.value,
                                                    "full-width": "",
                                                    "data-testid": "project-detail-description"
                                                }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"])
                                            ])
                                        ]),
                                        _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["header"]),
                                    _createVNode(_unref(WidgetShell), {
                                        header: _unref(t)('projects.detail.sections.perimeter')
                                    }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_10, [
                                                _createVNode(_unref(TextInput), {
                                                    modelValue: form.geographicArea,
                                                    "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event) => ((form.geographicArea) = $event)),
                                                    label: _unref(t)('projects.detail.geographicAreaLabel'),
                                                    maxlength: 200,
                                                    disabled: !canEdit.value,
                                                    "full-width": "",
                                                    "data-testid": "project-detail-geographic-area"
                                                }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"]),
                                                _createVNode(_unref(SelectInput), {
                                                    modelValue: form.territorialScope,
                                                    "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event) => ((form.territorialScope) = $event)),
                                                    label: _unref(t)('projects.detail.territorialScopeLabel'),
                                                    options: territorialScopeOptions.value,
                                                    disabled: !canEdit.value,
                                                    "full-width": "",
                                                    "data-testid": "project-detail-territorial-scope"
                                                }, null, 8 /* PROPS */, ["modelValue", "label", "options", "disabled"]),
                                                _createVNode(_unref(TextInput), {
                                                    modelValue: estimatedBeneficiariesInput.value,
                                                    "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event) => ((estimatedBeneficiariesInput).value = $event)),
                                                    label: _unref(t)('projects.detail.estimatedBeneficiariesLabel'),
                                                    inputmode: "numeric",
                                                    error: errors.estimatedBeneficiaries,
                                                    disabled: !canEdit.value,
                                                    "full-width": "",
                                                    "data-testid": "project-detail-estimated-beneficiaries",
                                                    onBlur: validateEstimatedBeneficiaries
                                                }, null, 8 /* PROPS */, ["modelValue", "label", "error", "disabled"]),
                                                _createVNode(_unref(TextInput), {
                                                    modelValue: form.targetAudience,
                                                    "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event) => ((form.targetAudience) = $event)),
                                                    label: _unref(t)('projects.detail.targetAudienceLabel'),
                                                    maxlength: 200,
                                                    disabled: !canEdit.value,
                                                    "full-width": "",
                                                    "data-testid": "project-detail-target-audience"
                                                }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"])
                                            ])
                                        ]),
                                        _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["header"])
                                ]),
                                _createElementVNode("div", _hoisted_11, [
                                    (project.value)
                                        ? (_openBlock(), _createBlock(ProjectMilestonesWidget, {
                                            key: 0,
                                            ref_key: "milestonesWidget",
                                            ref: milestonesWidget,
                                            "project-id": project.value.projectId,
                                            milestones: project.value.milestones,
                                            "can-edit": canEdit.value,
                                            onSaved: onMilestoneSaved,
                                            onDeleted: onMilestoneDeleted
                                        }, null, 8 /* PROPS */, ["project-id", "milestones", "can-edit"]))
                                        : _createCommentVNode("v-if", true),
                                    _createVNode(_unref(WidgetShell), {
                                        header: _unref(t)('projects.detail.sections.budget')
                                    }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_12, [
                                                _createElementVNode("div", _hoisted_13, [
                                                    _createVNode(_unref(TextInput), {
                                                        modelValue: objectiveAmountInput.value,
                                                        "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event) => ((objectiveAmountInput).value = $event)),
                                                        label: _unref(t)('projects.detail.objectiveAmountLabel'),
                                                        inputmode: "decimal",
                                                        error: errors.objectiveAmount,
                                                        disabled: !canEdit.value,
                                                        "full-width": "",
                                                        "data-testid": "project-detail-objective-amount",
                                                        onBlur: validateObjectiveAmount
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "error", "disabled"]),
                                                    _createElementVNode("div", _hoisted_14, [
                                                        _createElementVNode("span", _hoisted_15, _toDisplayString(_unref(t)("projects.detail.collectedAmountLabel")), 1 /* TEXT */),
                                                        _createElementVNode("span", _hoisted_16, _toDisplayString(collectedAmountDisplay.value), 1 /* TEXT */)
                                                    ]),
                                                    _createElementVNode("div", _hoisted_17, [
                                                        _createElementVNode("span", _hoisted_18, _toDisplayString(_unref(t)("projects.detail.varianceLabel")), 1 /* TEXT */),
                                                        _createElementVNode("span", {
                                                            class: _normalizeClass(["text-base font-semibold", variance.value !== null && variance.value < 0
                                                                    ? 'text-(--colors-text-error-strong)'
                                                                    : 'text-(--colors-text-success-strong)']),
                                                            "data-testid": "project-detail-variance"
                                                        }, _toDisplayString(varianceDisplay.value), 3 /* TEXT, CLASS */)
                                                    ]),
                                                    _createVNode(_unref(TextInput), {
                                                        modelValue: form.fundingSource,
                                                        "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event) => ((form.fundingSource) = $event)),
                                                        label: _unref(t)('projects.create.fundingSourceLabel'),
                                                        maxlength: 200,
                                                        disabled: !canEdit.value,
                                                        "full-width": "",
                                                        "data-testid": "project-detail-funding-source"
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"])
                                                ]),
                                                _createElementVNode("div", _hoisted_19, [
                                                    _createVNode(_unref(TextInput), {
                                                        modelValue: form.chargeAccount,
                                                        "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event) => ((form.chargeAccount) = $event)),
                                                        label: _unref(t)('projects.create.chargeAccountLabel'),
                                                        maxlength: 50,
                                                        disabled: !canEdit.value,
                                                        "full-width": "",
                                                        "data-testid": "project-detail-charge-account"
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"]),
                                                    _createVNode(_unref(TextInput), {
                                                        modelValue: form.analyticalCode,
                                                        "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event) => ((form.analyticalCode) = $event)),
                                                        label: _unref(t)('projects.create.analyticalCodeLabel'),
                                                        maxlength: 50,
                                                        disabled: !canEdit.value,
                                                        "full-width": "",
                                                        "data-testid": "project-detail-analytical-code"
                                                    }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"])
                                                ]),
                                                _createVNode(_unref(Textarea), {
                                                    modelValue: form.financialComments,
                                                    "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event) => ((form.financialComments) = $event)),
                                                    label: _unref(t)('projects.detail.financialCommentsLabel'),
                                                    rows: 3,
                                                    height: "auto",
                                                    "max-length": 2000,
                                                    disabled: !canEdit.value,
                                                    "full-width": "",
                                                    "data-testid": "project-detail-financial-comments"
                                                }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"])
                                            ])
                                        ]),
                                        _: 1 /* STABLE */
                                    }, 8 /* PROPS */, ["header"])
                                ])
                            ]),
                            (showSaveBar.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                    _createElementVNode("p", _hoisted_21, [
                                        _cache[23] || (_cache[23] = _createElementVNode("span", {
                                            class: "ri-edit-circle-line text-sm text-(--colors-text-warning)",
                                            "aria-hidden": "true"
                                        }, null, -1 /* CACHED */)),
                                        _createTextVNode(" " + _toDisplayString(_unref(t)("projects.detail.unsavedChanges")), 1 /* TEXT */)
                                    ]),
                                    _createElementVNode("div", _hoisted_22, [
                                        _createVNode(_unref(Button), {
                                            variant: "secondary",
                                            size: "sm",
                                            label: _unref(t)('form.cancel'),
                                            "data-testid": "project-detail-save-bar-cancel",
                                            onClick: _cache[21] || (_cache[21] = ($event) => (saveBarDismissed.value = true))
                                        }, null, 8 /* PROPS */, ["label"]),
                                        _createVNode(_unref(Button), {
                                            variant: "primary",
                                            size: "sm",
                                            label: _unref(t)('form.save'),
                                            disabled: !isFormValid.value || _unref(isSaving),
                                            loading: _unref(isSaving),
                                            "data-testid": "project-detail-save",
                                            onClick: _unref(saveAll)
                                        }, null, 8 /* PROPS */, ["label", "disabled", "loading", "onClick"])
                                    ])
                                ]))
                                : _createCommentVNode("v-if", true)
                        ], 8 /* PROPS */, _hoisted_5), [
                            [_vShow, activeTab.value === 'details']
                        ])
                    ], 64 /* STABLE_FRAGMENT */)),
                _createVNode(_unref(UnsavedChangesModal), {
                    modelValue: showUnsavedChangesModal.value,
                    "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event) => ((showUnsavedChangesModal).value = $event)),
                    title: _unref(t)('leaveWithoutSaving.title'),
                    message: _unref(t)('leaveWithoutSaving.message'),
                    "stay-label": _unref(t)('leaveWithoutSaving.stay'),
                    "leave-label": _unref(t)('leaveWithoutSaving.leave'),
                    "save-label": _unref(t)('leaveWithoutSaving.save'),
                    "save-disabled": _unref(isSaving) || !isFormValid.value,
                    onDiscard: handleLeave,
                    onSave: handleSaveAndContinue,
                    onStay: handleStay
                }, null, 8 /* PROPS */, ["modelValue", "title", "message", "stay-label", "leave-label", "save-label", "save-disabled"])
            ]));
        };
    }
});
